import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { SignedIn, SignedOut, RedirectToSignIn, UserProfile as ClerkUserProfile, PricingTable } from "@clerk/clerk-react";
import PricingPage from "./components/pricing/PricingPage";
import ServiciosMantenedor from "./components/servicios/ServiciosMantenedor";
import RepuestosMantenedor from "./components/repuestos/RepuestosMantenedor";
import MecanicosMantenedor from "./components/mecanicos/MecanicosMantenedor";
import VehiculosMantenedor from "./components/vehiculos/VehiculosMantenedor";
import UserProfile from "./components/profile/UserProfile";
import CompleteProfile from "./components/profile/CompleteProfile";
import { LandingB2B, LandingB2C } from "./components/landing";
import ToastContainer from "./components/common/ToastContainer";
import { SucursalProvider } from "./context/SucursalContext";
import Citas from "./components/citas/Citas";
import LogoMantenedor from "./components/configuracion/LogoMantenedor";
import ConfiguracionMantenedor from "./components/configuracion/ConfiguracionMantenedor";
import { Dashboard } from "./components/dashboard";
import MantenimientosPage from "./components/mantenimientos/MantenimientosPage";
import ClientesMantenedorMultiEmpresa from "./components/clientes/ClientesMantenedorMultiEmpresa";
import RepuestosExternosPage from "./components/repuestos/RepuestosExternosPage";
import RepuestosMarketplace from "./components/repuestos/marketplace/RepuestosMarketplace";
import { useZoom } from "./hooks/useZoom";
import SEOWrapper from "./components/common/SEOWrapper";
import ChatIA from "./components/common/ChatIA";

// Context Providers - SIMPLIFICADO
import { SimpleEmpresaProvider, useSimpleEmpresa } from "@/hooks/useSimpleEmpresa";
import SimpleAuthWrapper from "@/components/auth/SimpleAuthWrapper";
import { ProtectedRoute, UnauthorizedPage } from "@/components/auth";
import EmpresaSelectorComplete from "@/components/auth/EmpresaSelectorComplete";
import SolicitarAcceso from "@/components/auth/SolicitarAcceso";
import SelectUserType from "@/components/auth/SelectUserType";
import EmpresaOnboardingComplete from "@/components/onboarding/EmpresaOnboardingComplete";

// Importar estilos CSS
import './styles/App.css';
import './styles/SidebarStyles.css';
import './styles/select-fix.css';

function ClerkApp() {
  console.log('🎪 ClerkApp render - timestamp:', new Date().toLocaleTimeString());

  return (
    <SimpleEmpresaProvider>
      <SucursalProvider>
        {/* Toast Container siempre visible */}
        <ToastContainer />
        
        <SEOWrapper>
          <SimpleAuthWrapper>
            <Routes>
                {/* Landing Pages - Públicas */}
                <Route path="/landing" element={<Navigate to="/taller" />} />
                <Route path="/taller" element={<LandingB2B />} />
                <Route path="/cliente" element={<LandingB2C />} />
                
                
                {/* Rutas del sistema multi-empresa */}
                <Route path="/unauthorized" element={<UnauthorizedPage />} />
                <Route path="/select-empresa" element={<EmpresaSelectorComplete />} />
                <Route path="/select-user-type" element={<SelectUserType />} />
                <Route path="/solicitar-acceso" element={<SolicitarAcceso />} />
                <Route path="/onboarding/empresa" element={<EmpresaOnboardingComplete />} />
                
                {/* Debug Routes - Se mueven después para que estén dentro del EmpresaMiddleware */}
                
                {/* Ruta para completar perfil después del registro */}
                <Route path="/complete-profile" element={
                <SignedIn>
                    <CompleteProfile />
                </SignedIn>
                } />
                
                {/* Dashboard - Ruta principal */}
                <Route path="/" element={<Navigate to="/dashboard" />} />
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <DashboardOrProfile />
                  </ProtectedRoute>
                } />
                
                {/* Rutas protegidas con permisos específicos */}
                <Route path="/clientes" element={
                  <ProtectedRoute requiredPermission="clientes.ver">
                    <ClientesMantenedorMultiEmpresa />
                  </ProtectedRoute>
                } />
                
                <Route path="/repuestos-externos" element={
                  <ProtectedRoute requiredPermission="repuestos.ver">
                    <RepuestosExternosPage />
                  </ProtectedRoute>
                } />
                
                <Route path="/marketplace-repuestos" element={
                  <ProtectedRoute requiredPermission="repuestos.ver">
                    <RepuestosMarketplace />
                  </ProtectedRoute>
                } />
                
                <Route path="/vehiculos" element={
                  <ProtectedRoute requiredPermission="vehiculos.ver">
                    <VehiculosMantenedor />
                  </ProtectedRoute>
                } />
                
                <Route path="/vehiculos/:clienteId" element={
                  <ProtectedRoute requiredRole="management">
                    <VehiculosMantenedor />
                  </ProtectedRoute>
                } />
                
                <Route path="/citas" element={
                  <ProtectedRoute requiredPermission="citas.ver">
                    <Citas />
                  </ProtectedRoute>
                } />
                
                <Route path="/servicios" element={
                  <ProtectedRoute requiredRole="admin">
                    <ServiciosMantenedor />
                  </ProtectedRoute>
                } />

                <Route path="/repuestos" element={
                  <ProtectedRoute requiredRole="admin">
                    <RepuestosMantenedor />
                  </ProtectedRoute>
                } />

                <Route path="/mecanicos" element={
                  <ProtectedRoute requiredRole="admin">
                    <MecanicosMantenedor />
                  </ProtectedRoute>
                } />
                
                <Route path="/mantenimientos" element={
                  <ProtectedRoute requiredPermission="mantenimientos.ver">
                    <MantenimientosPage />
                  </ProtectedRoute>
                } />
                
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <UserProfile />
                  </ProtectedRoute>
                } />

                {/* Ruta para Pricing Page - Con layout sidebar */}
                <Route path="/pricing" element={
                  <ProtectedRoute>
                    <PricingPage />
                  </ProtectedRoute>
                } />

                {/* Ruta para UserProfile de Clerk con billing - B2C SaaS */}
                <Route path="/user-profile" element={
                  <SignedIn>
                    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
                      <ClerkUserProfile />
                    </div>
                  </SignedIn>
                } />
                
                {/* Rutas de configuración */}
                <Route path="/configuracion/logo" element={
                  <ProtectedRoute requiredRole="admin">
                    <LogoMantenedor />
                  </ProtectedRoute>
                } />
                
                <Route path="/configuracion/general" element={
                  <ProtectedRoute requiredRole="admin">
                    <ConfiguracionMantenedor />
                  </ProtectedRoute>
                } />
                
                {/* Ruta por defecto - redirige a landing taller */}
                <Route path="*" element={<Navigate to="/taller" />} />
              </Routes>
            </SimpleAuthWrapper>
            
            {/* Chat IA Flotante - Disponible globalmente */}
            <ChatIA />
          </SEOWrapper>
      </SucursalProvider>
    </SimpleEmpresaProvider>
  );
}

// Componente para mostrar Dashboard o UserProfile según el tipo de usuario
const DashboardOrProfile = () => {
  const { isSuperAdmin, isAdmin } = useSimpleEmpresa();
  
  console.log('🏠 DashboardOrProfile - isSuperAdmin:', isSuperAdmin, 'isAdmin:', isAdmin);
  
  // SuperAdmin y Admin ven el Dashboard
  if (isSuperAdmin || isAdmin) {
    console.log('🏠 Mostrando Dashboard para SuperAdmin/Admin');
    return <Dashboard />;
  }
  
  // Otros usuarios (clientes, mecánicos) ven su perfil
  console.log('🏠 Mostrando UserProfile para usuario regular');
  return <UserProfile />;
};

export default ClerkApp;